import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Vehicle } from 'types/vehicle';
import { useAddTransferNoteMutation } from 'services/api/transfer-report';
import api from 'services/api';
import { getUser } from 'utils/auth';
import toast from 'react-hot-toast';
import { ProgressStepper, VehicleNotes } from 'components/vehicle-history';
import type { NoteItem } from 'components/vehicle-history';

export type { NoteItem };

export interface TrackingNoteItem {
  id: number;
  message: string;
  created_at?: string;
  timestamp?: string;
}

export interface TransferRecord {
  id?: number;
  notes?: string | null;
  date_sold?: string | null;
  date_title_received?: string | null;
  date_title_sent_out?: string | null;
  title_sent_to?: string | null;
  date_pmt_dollar_received?: string | null;
  usps_fed_ex_tracking?: string | null;
  status?: string;
  has_documents?: boolean;
  notesList?: NoteItem[];
  trackingNotesList?: TrackingNoteItem[] | null;
  title_delivered?: boolean;
}

type QueryEndpoint = 'getAllBuyerTransactions' | 'getEasyPayBuyerTransactions' | 'getCardBuyerTransactions';
type ReduxTab = 'all' | 'easyPay' | 'card';

interface VehicleTitleBlockProps {
  vehicle: Vehicle & { transferRecord?: TransferRecord };
  queryEndpoint?: QueryEndpoint;
  reduxTab?: ReduxTab;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PurchasedBadge = styled.span`
  background: #e0e0e0;
  color: #000;
  font-size: 16px;
  font-weight: 700;
  border-radius: 20px;
  padding: 6px 14px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const TitleLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #000;
`;

const TitleLink = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #005a96;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

/* ── More Info ────────────────────────────────────────────────────── */

const MoreInfoToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  color: #3994de;
  text-decoration: underline;
  text-decoration-style: solid;
`;

const CaretIcon = styled.svg`
  flex-shrink: 0;
`;

const TrackingNotesList = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 4px;
`;

const TrackingNoteRow = styled.div<{ $isLast: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  padding-left: 18px;

  ${({ $isLast }) =>
    !$isLast &&
    `
    &::after {
      content: '';
      position: absolute;
      left: 3px;
      top: 10px;
      bottom: -2px;
      border-left: 1px dotted #cccccc;
    }
  `}
`;

const TrackingNoteDot = styled.div`
  position: absolute;
  left: 0;
  top: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #c4c4c4;
  z-index: 1;
`;

const TrackingNoteContent = styled.div<{ $isLast: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: ${({ $isLast }) => ($isLast ? '0' : '16px')};
`;

const TrackingNoteText = styled.span`
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  color: #666666;
`;

const TrackingNoteDate = styled.span`
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  color: #666666;
`;

/* ── helpers ──────────────────────────────────────────────────────── */

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  const m = moment.utc(dateStr);
  return m.isValid() ? m.format('MM/DD/YYYY') : '';
};

/* ── component ────────────────────────────────────────────────────── */

const VehicleTitleBlock = ({
  vehicle,
  queryEndpoint = 'getAllBuyerTransactions',
  reduxTab = 'all'
}: VehicleTitleBlockProps) => {
  const tr = vehicle.transferRecord;
  const dispatch = useDispatch();
  const { page, perPage } = useSelector((state: any) => state.transactions[reduxTab]);
  const [addNote, { isLoading }] = useAddTransferNoteMutation();

  const currentUser = getUser();

  const steps = [
    { label: 'Sell date', active: true, date: formatDate(tr?.date_sold) },
    { label: 'Title Received', active: !!tr?.date_title_received, date: formatDate(tr?.date_title_received) },
    {
      label: 'Title Sent out',
      active: !!tr?.date_title_sent_out,
      date: formatDate(tr?.date_title_sent_out),
      tooltip: tr?.usps_fed_ex_tracking ?? undefined
    },
    { label: 'Title delivered', active: !!tr?.title_delivered, date: '' }
  ];

  const purchasedDate = formatDate(tr?.date_sold);
  const notesList = tr?.notesList ?? [];
  const trackingNotes = tr?.trackingNotesList ?? [];
  const titleSentOutActive = !!tr?.date_title_sent_out;
  const showMoreInfoToggle = titleSentOutActive && trackingNotes.length > 0;
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);

  const handleSend = async (message: string) => {
    const newNote = await addNote({
      id: tr!.id!,
      payload: { message, visible_to_seller: false, visible_to_buyer: true }
    }).unwrap();

    const userName =
      currentUser?.name || `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Me';

    const noteItem: NoteItem = {
      id: newNote.id,
      message,
      created_at: newNote.created_at || newNote.timestamp || new Date().toISOString(),
      sender: { id: currentUser?.id, role: 'buyer', name: userName }
    };

    dispatch(
      (api as any).util.updateQueryData(queryEndpoint, { limit: perPage, offset: page }, (draft: any) => {
        const entity = draft.rows.entities[vehicle.id];
        if (entity?.transferRecord) {
          entity.transferRecord.notesList = [...(entity.transferRecord.notesList ?? []), noteItem];
        }
      })
    );
  };

  const handleSendWithToast = async (message: string) => {
    try {
      await handleSend(message);
    } catch (e) {
      toast.error('Failed to send note. Please try again.');
      throw e;
    }
  };

  return (
    <Wrapper>
      <TopRow>{purchasedDate && <PurchasedBadge>Purchased: {purchasedDate}</PurchasedBadge>}</TopRow>
      {/* <TitleRow>
        <TitleLabel>Vehicle Title</TitleLabel>
        {tr?.has_documents && tr?.id ? (
          <TitleLink onClick={() => window.open(`/buyer/transfer-report/${tr.id}/document`, '_blank')}>
            See title copy
          </TitleLink>
        ) : (
          <span style={{ fontSize: 16, fontWeight: 400, color: '#999' }}>No copy available yet.</span>
        )}
      </TitleRow> */}
      <ProgressStepper steps={steps} notes={tr?.notes ?? undefined} />
      {showMoreInfoToggle && (
        <>
          {moreInfoOpen && (
            <TrackingNotesList>
              {trackingNotes.map((note, i) => {
                const dateStr = note.created_at || note.timestamp;
                const formatted = dateStr && moment(dateStr).isValid()
                  ? moment(dateStr).format('MM/DD/YYYY HH:mm')
                  : '';
                const isLast = i === trackingNotes.length - 1;
                return (
                  <TrackingNoteRow key={note.id} $isLast={isLast}>
                    <TrackingNoteDot />
                    <TrackingNoteContent $isLast={isLast}>
                      <TrackingNoteText>{note.message}</TrackingNoteText>
                      {formatted && <TrackingNoteDate>{formatted}</TrackingNoteDate>}
                    </TrackingNoteContent>
                  </TrackingNoteRow>
                );
              })}
            </TrackingNotesList>
          )}
          <MoreInfoToggle onClick={() => setMoreInfoOpen(v => !v)}>
            {moreInfoOpen ? 'Less Info' : 'More Info'}
            <CaretIcon
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {moreInfoOpen
                ? <path d="M9 7.5L6 4.5L3 7.5" stroke="#3994DE" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                : <path d="M3 4.5L6 7.5L9 4.5" stroke="#3994DE" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              }
            </CaretIcon>
          </MoreInfoToggle>
        </>
      )}
      <VehicleNotes
        notesList={notesList}
        currentRole="buyer"
        onSend={handleSendWithToast}
        isSending={isLoading}
        canSend={!!tr?.id}
      />
    </Wrapper>
  );
};

export default VehicleTitleBlock;
